import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import SideNav from './SideNav';
import Dashboard from '../pages/Dashboard/Dashboard';
import Faculty from '../pages/Faculty/Faculty';
import Students from '../pages/Students/Students';
import Users from '../pages/Users/Users';
import Fees from '../pages/Fees/Fees';
import Transportation from '../pages/Transportation/Transportation';
import Admission from '../pages/Admission/admission';
import CourseContent from '../pages/CourseContent/CourseContent';
import Assessments from '../pages/Assessments/Assessments';
import Topbar from './Topbar';
import Events from '../pages/Events/Events';
import Notifications from '../pages/Notifications/Notifications';
import UploadData from '../pages/UploadData/UploadData';
import { useAuth } from '../AuthContext';
import EditStudentForm from '../pages/Students/EditStudentForm';
import StudentBulkImport from '../pages/Students/StudentBulkImport';
import FacultyBulkImport from '../pages/Faculty/FacultyBulkImport';
import StaffBulkImport from '../pages/Faculty/StaffBulkImport';
import SettingsPage from '../pages/Settings/SettingsPage';
import ProtectedRoute from './ProtectedRoute';
import UserRegistrationModule from '../pages/Users/UserRegistrationModule';
import StudentDashboard from '../pages/Dashboard/StudentDashboard';
import FacultyDashboard from '../pages/Dashboard/FacultyDashboard';
import Timetable from '../pages/Classes/Timetable';
import Subjects from '../pages/Subjects/Subjects';
import Classes from '../pages/Classes/Classs';
import SubjectView from '../pages/Subjects/SubjectView';
import ClassDetails from '../pages/Classes/ClassDetails'; // Import ClassDetails
import ClassSectionList from '../pages/Classes/ClassSectionList';
import EventDetails from '../pages/Events/EventDetails';
import Staff from '../pages/Staff/Staff';
import Admin from '../pages/Staff/Admin';
import StudentPerformance from '../pages/Examinations/StudentPerformance';
import ExamAssessmentManager from '../pages/Examinations/ExamAssessmentManager';
import ExamTabsContainer from '../pages/Examinations/ExamTabsContainer';
import FeeAnalytics from '../pages/Fees/FeeAnalytics';
import StudentPayments from '../pages/Fees/StudentPayments';
import FeeBulkImport from '../pages/Fees/FeeBulkImport';
import FeeConcessions from '../pages/Fees/FeeConcessions';
import BulkImportHub from '../pages/BulkImport/BulkImportHub';
import LocalAIAgents from '../pages/LocalAIAgents/LocalAIAgents';
import DocAIAssistant from '../pages/LocalAIAgents/DocAIAssistant';
import ERPInbox from '../pages/Notifications/ERPInbox';
import StudentInbox from '../pages/Notifications/StudentInbox';
import LoadingScreen from './Common/LoadingScreen';
import ErrorBoundary from './Common/SafeApp/ErrorBoundary';
import MarkAttendance from '../pages/Attendance/MarkAttendance';
import AttendanceRegister from '../pages/Attendance/AttendanceRegister';
import AttendanceReports from '../pages/Attendance/AttendanceReports';
import AttendanceAudit from '../pages/Attendance/AttendanceAudit';
import StudentAttendanceView from '../pages/Attendance/StudentAttendanceView';
import CenterVisits from '../pages/Attendance/CenterVisits';
import ServicesStatus from '../pages/Status/ServicesStatus';
import TeacherSchedules from '../pages/Schedules/TeacherSchedules';
import FirebaseAnalytics from '../pages/Analytics/FirebaseAnalytics';

// Component to handle role-based dashboard redirects
const DashboardRedirect = ({ userRole }) => {
  const { logout } = useAuth();

  useEffect(() => {
    if (!userRole) {
      const timer = setTimeout(() => {
        console.warn('[DashboardRedirect] No valid userRole resolved. Logging out to login page.');
        logout();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [userRole, logout]);

  if (!userRole) {
    return <LoadingScreen title="Personalizing your Workspace" subtitle="Setting up dashboard..." />;
  }
  const role = String(userRole).toUpperCase();
  switch (role) {
    case 'STUDENT':
      return <Navigate to="/student-dashboard" replace />;
    case 'FACULTY':
    case 'TEACHER':
      return <Navigate to="/faculty-dashboard" replace />;
    case 'ADMIN':
    case 'STAFF':
    case 'NIRVAHAK':
    case 'SUPERADMIN':
    default:
      return <Navigate to="/admin-dashboard" replace />;
  }
};

const Main = () => {
  const { userRole, user, isLoading, orgId, setOrgId, parentOrgId } = useAuth();
  const effectiveParentOrgId = parentOrgId || user?.parentOrgId || user?.orgDetails?.parentOrgId || '';
  const isSmallOrg = effectiveParentOrgId === 'SMALLORG-001';

  // Initialize orgId from user's orgId when user data is available
  // Reset orgId when user logs out (user becomes null)
  useEffect(() => {
    if (!user) {
      // User logged out, reset orgId
      setOrgId('');
    } else if (user && user.orgId) {
      // Initialize orgId from user's orgId if not already set
      // This allows manual org selection to persist until logout
      if (!orgId) {
        setOrgId(user.orgId);
      }
    }
  }, [user, orgId]);

  const isAdmin = userRole === 'ADMIN';
  const isStudent = userRole === 'STUDENT';
  const isFaculty = userRole === 'FACULTY';
  const isStaff = userRole === 'STAFF';
  const isManagement = isAdmin || isStaff;

  // Mobile sidebar state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  if (isLoading) {
    return <LoadingScreen title="Personalizing your Workspace" subtitle="Syncing your records and permissions..." />;
  }

  const handleOrgIdSelect = (selectedOrgId) => {
    setOrgId(selectedOrgId);
  };


  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  return (
    <BrowserRouter>
      <div className="main">
        <SideNav
          mobileOpen={mobileOpen}
          onClose={handleDrawerClose}
          onCollapseChange={setIsSidebarCollapsed}
        />
        <div className={`asideWrap ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Topbar
            userOrgId={orgId}
            currentOrgId={orgId}
            onOrgIdSelect={handleOrgIdSelect}
            onMenuClick={handleDrawerToggle}
          />
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<DashboardRedirect userRole={userRole} />} />
              <Route path="/student-dashboard" element={
                isStudent ? <StudentDashboard orgId={orgId} /> : <Navigate to="/admin-dashboard" replace />
              } />
              <Route path="/faculty-dashboard" element={
                isFaculty ? <FacultyDashboard orgId={orgId} /> : <Navigate to="/admin-dashboard" replace />
              } />
              <Route path="/admin-dashboard" element={
                <Dashboard orgId={orgId} />
              } />
              <Route path="/Users" element={
                <ProtectedRoute module="USERS" action="VIEW">
                  <Users />
                </ProtectedRoute>
              } />
              <Route path="/Faculty" element={
                <ProtectedRoute module="FACULTY" action="VIEW">
                  <Faculty orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/kv-faculty/:type" element={
                <ProtectedRoute module="FACULTY" action="VIEW">
                  <Faculty orgId={orgId} isKvFaculty={true} />
                </ProtectedRoute>
              } />
              <Route path="/kv-faculty" element={<Navigate to="/kv-faculty/samanvaykarta" replace />} />
              <Route path="/Faculty/registration" element={
                <ProtectedRoute module="FACULTY" action="CREATE">
                  <UserRegistrationModule userType="FACULTY" orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/Faculty/bulk-import" element={
                <ProtectedRoute module="FACULTY" action="BULK_IMPORT">
                  <FacultyBulkImport orgId={orgId} />
                </ProtectedRoute>
              } />
              {/* === STAFF - NEW SEPARATE SECTION === */}
              <Route path="/Staff" element={
                isSmallOrg ? (
                  <Navigate to="/Students" replace />
                ) : (
                  <ProtectedRoute module="FACULTY" action="VIEW"> <Staff orgId={orgId} /></ProtectedRoute>
                )
              } />
              <Route path="/Admin" element={<ProtectedRoute module="FACULTY" action="VIEW"> <Admin orgId={orgId} /></ProtectedRoute>} />
              <Route path="/Admin/registration" element={
                <ProtectedRoute module="FACULTY" action="CREATE">
                  <UserRegistrationModule userType="ADMIN" orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/Staff/registration" element={
                isSmallOrg ? (
                  <Navigate to="/Students" replace />
                ) : (
                  <ProtectedRoute module="FACULTY" action="CREATE">
                    <UserRegistrationModule userType="STAFF" orgId={orgId} />
                  </ProtectedRoute>
                )
              } />
              <Route path="/Staff/bulk-import" element={
                isSmallOrg ? (
                  <Navigate to="/Students" replace />
                ) : (
                  <ProtectedRoute module="FACULTY" action="BULK_IMPORT">
                    <StaffBulkImport orgId={orgId} />
                  </ProtectedRoute>
                )
              } />
              <Route path="/Students" element={
                <ProtectedRoute module="STUDENTS" action="VIEW">
                  <Students orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/students-by-section" element={<Navigate to="/Students" replace />} />
              <Route path="/Students/registration" element={
                <ProtectedRoute module="STUDENTS" action="CREATE">
                  <UserRegistrationModule userType="STUDENT" orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/Students/editstudent/" element={
                <ProtectedRoute module="STUDENTS" action="UPDATE">
                  <EditStudentForm orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/Students/bulk-import" element={
                <ProtectedRoute module="STUDENTS" action="BULK_IMPORT">
                  <StudentBulkImport orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/Admission" element={
                <ProtectedRoute module="ADMISSION" action="VIEW">
                  <Admission orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/coursecontent" element={
                <ProtectedRoute module="COURSE_CONTENT" action="VIEW">
                  <CourseContent orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/events" element={<Events orgId={orgId} />} />
              <Route path="/events/details" element={<EventDetails orgId={orgId} />} />
              <Route path="/performance" element={
                isSmallOrg ? (
                  <Navigate to="/" replace />
                ) : (
                  <StudentPerformance orgId={orgId} />
                )
              } />
              <Route path="/exams" element={
                isSmallOrg ? (
                  <Navigate to="/" replace />
                ) : (
                  <ExamTabsContainer orgId={orgId} />
                )
              } />


              <Route path="/events/details" element={<EventDetails orgId={orgId} />} />
              <Route path="/timetable/:className/:sectionName/:classId" element={
                <ProtectedRoute module="CLASSES" action="VIEW">
                  <Timetable orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <Notifications orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/erp-inbox" element={
                <ProtectedRoute>
                  <ERPInbox />
                </ProtectedRoute>
              } />
              <Route path="/student-inbox" element={
                isStudent ? <StudentInbox /> : <Navigate to="/" replace />
              } />
              {/* <Route path="/courses/:courseId" element={
                <ProtectedRoute module="ADMISSION" action="VIEW">
                  <CourseDetails orgId={orgId} />
                </ProtectedRoute>
              } /> */}
              <Route path="/finance" element={
                <ProtectedRoute module="FEES" action="VIEW">
                  <Fees orgId={orgId} initialTab={0} />
                </ProtectedRoute>
              } />
              <Route path="/finance/concessions" element={
                <ProtectedRoute module="FEES" action="VIEW">
                  <Fees orgId={orgId} initialTab={1} />
                </ProtectedRoute>
              } />
              <Route path="/fee-concessions" element={
                <ProtectedRoute module="FEES" action="VIEW">
                  <Fees orgId={orgId} initialTab={1} />
                </ProtectedRoute>
              } />
              <Route path="/feeanalytics" element={
                isSmallOrg ? (
                  <Navigate to="/finance" replace />
                ) : (
                  <ProtectedRoute module="FEES" action="VIEW">
                    <FeeAnalytics orgId={orgId} />
                  </ProtectedRoute>
                )
              } />
              <Route path="/student-payments" element={
                <ProtectedRoute module="FEES" action="VIEW">
                  <StudentPayments orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/finance/student-payments" element={
                <ProtectedRoute module="FEES" action="VIEW">
                  <StudentPayments orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/finance/bulk-import" element={
                <ProtectedRoute module="FEES" action="BULK_IMPORT">
                  <FeeBulkImport />
                </ProtectedRoute>
              } />
              <Route path="/bulk-import" element={
                <ProtectedRoute module="ADMIN" action="VIEW">
                  <BulkImportHub orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/Faculty-Assistant" element={
                <ProtectedRoute module="ASSESSMENTS" action="VIEW">
                  <Assessments orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/local-ai-agents" element={
                <ProtectedRoute module="ASSESSMENTS" action="VIEW">
                  <LocalAIAgents />
                </ProtectedRoute>
              } />
              <Route path="/doc-ai-assistant" element={
                <ProtectedRoute module="ASSESSMENTS" action="VIEW">
                  <DocAIAssistant />
                </ProtectedRoute>
              } />
              <Route path="/transportation" element={
                isSmallOrg ? (
                  <Navigate to="/" replace />
                ) : (
                  <ProtectedRoute module="TRANSPORTATION" action="VIEW">
                    <Transportation orgId={orgId} />
                  </ProtectedRoute>
                )
              } />
              <Route path="/classes" element={
                <ProtectedRoute module="CLASSES" action="VIEW">
                  <Classes orgId={orgId} />
                </ProtectedRoute>
              } />

              <Route path="/class-section-list" element={
                <ProtectedRoute module="CLASSES" action="VIEW">
                  <ClassSectionList orgId={orgId} />
                </ProtectedRoute>
              } />

              <Route path="/classes/:classInfo" element={
                <ProtectedRoute module="CLASSES" action="VIEW">
                  <ClassDetails orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/academics/schedules" element={
                <ProtectedRoute>
                  <TeacherSchedules orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/courses" element={
                <ProtectedRoute module="SUBJECTS" action="VIEW">
                  <Subjects orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/courses/:courseCode" element={
                <SubjectView orgId={orgId} />
              } />
              {/* === ATTENDANCE MODULE === */}
              <Route path="/attendance/mark" element={
                <ProtectedRoute>
                  <MarkAttendance orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/attendance/center-visits" element={
                <ProtectedRoute>
                  <CenterVisits orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/attendance/register" element={
                <ProtectedRoute>
                  <AttendanceRegister orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/attendance/reports" element={
                <ProtectedRoute>
                  <AttendanceReports orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/attendance/admin-corrections" element={
                <ProtectedRoute>
                  <AttendanceAudit />
                </ProtectedRoute>
              } />
              <Route path="/attendance/student" element={
                <ProtectedRoute>
                  <StudentAttendanceView />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute module="SETTINGS" action="VIEW">
                  <SettingsPage
                    orgId={orgId}
                    onOrgIdSelect={handleOrgIdSelect}
                  />
                </ProtectedRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <FirebaseAnalytics orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/firebase-analytics" element={
                <ProtectedRoute>
                  <FirebaseAnalytics orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/services-status" element={
                isSmallOrg ? (
                  <Navigate to="/" replace />
                ) : (
                  <ProtectedRoute>
                    <ServicesStatus />
                  </ProtectedRoute>
                )
              } />
              <Route path="/UploadData" element={
                <ProtectedRoute module="UPLOAD_DATA" action="VIEW">
                  <UploadData orgId={orgId} />
                </ProtectedRoute>
              } />
              <Route path="/unauthorized" element={
                <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                  <h2>Access Restricted</h2>
                  <p style={{ color: '#64748b', marginTop: '10px' }}>
                    You do not have permission to view this section. Please contact your system administrator.
                  </p>
                </div>
              } />
            </Routes>
          </ErrorBoundary>
        </div>
      </div>
    </BrowserRouter>
  );
};

export default Main;
